import React from 'react';
import { Avatar, Box, Typography, Paper, Grid2 } from '@mui/material';
import { Appointment, Employee, Service, createEmptyAppointment } from '../../types';

interface EmployeePickerProps {
    employeeSelected: Employee | null;
    isAnyAvailableSelected: boolean;
    allowAnyAvailable?: boolean;
    employees: Employee[];
    setSelectedEmployee: (selectedEmployee: Employee | null) => void;
    setIsAnyAvailableSelected: (selected: boolean) => void;
    setSelectedServicesList: (selectedServicesList: Service[] | null) => void;
    profilePictures: Record<string, string>;
    goToNextStep: () => void;
    currentAppointment: Appointment | null;
    handleAppointment: (appointment: Appointment | null) => void;
    selectLabel?: string;
    anyAvailableLabel?: string;
}

const EmployeePicker: React.FC<EmployeePickerProps> = ({
    employeeSelected,
    isAnyAvailableSelected,
    allowAnyAvailable = false,
    employees,
    setSelectedEmployee,
    setIsAnyAvailableSelected,
    setSelectedServicesList,
    profilePictures,
    goToNextStep,
    currentAppointment,
    handleAppointment,
    selectLabel = 'Select stylist',
    anyAvailableLabel = 'Any Available',
}) => {

const handleEmployeeSelect = (employee: Employee) => {
    const isDifferentEmployee = employee.id !== employeeSelected?.id;
    setIsAnyAvailableSelected(false);
    setSelectedEmployee(employee);

    if (isDifferentEmployee) {
        setSelectedServicesList(null);
        const notes = currentAppointment?.notes || '';
        handleAppointment(createEmptyAppointment({ employeeID: employee.id, notes }));
    }

    goToNextStep();
};

const handleAnyAvailableSelect = () => {
    setIsAnyAvailableSelected(true);
    setSelectedEmployee(null);
    setSelectedServicesList(null);
    const notes = currentAppointment?.notes || '';
    handleAppointment(createEmptyAppointment({ notes }));
    goToNextStep();
};

return (
    <>
        <Typography
            variant="overline"
            align="center"
            display="block"
            sx={{
                marginBottom: 2,
                borderRadius: '10px',
                textTransform: 'uppercase',
            }}
        >
            {selectLabel}
        </Typography>
        <Grid2 container spacing={2} justifyContent="center">
            {allowAnyAvailable && (
                <Grid2 container size={12} justifyContent="center">
                    <Paper
                        onClick={handleAnyAvailableSelect}
                        elevation={0}
                        sx={{
                        width: '100%',
                        cursor: 'pointer',
                        px: 2,
                        py: 2,
                        borderBottom: '1px solid #ccc',
                        backgroundColor: isAnyAvailableSelected ? '#f9f9f9' : 'transparent',
                        borderLeft: isAnyAvailableSelected ? '4px solid #000' : '4px solid transparent',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        }}
                    >
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <Typography variant="h6" textAlign="center">
                                {anyAvailableLabel}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid2>
            )}
            {employees.map((employee) => (

                <Grid2 container key={employee.id} size={12} justifyContent="center">
                    <Paper
                        onClick={() => handleEmployeeSelect(employee)}
                        elevation={0}
                        sx={{
                        width: '100%',
                        cursor: 'pointer',
                        px: 2,
                        py: 2,
                        borderBottom: '1px solid #ccc',
                        backgroundColor: employeeSelected?.id === employee.id ? '#f9f9f9' : 'transparent',
                        borderLeft: employeeSelected?.id === employee.id ? '4px solid #000' : '4px solid transparent',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        }}
                    >
                        <Box display="flex" justifyContent="center" alignItems="center">
                            {profilePictures[employee.id] && (
                                <Avatar
                                    src={profilePictures[employee.id]}
                                    alt={`${employee.firstName} ${employee.lastName}`}
                                    sx={{ width: 64, height: 64, marginRight: 1.5 }}
                                    />
                                )}
                            <Typography variant="h6" textAlign="center">
                            {employee.firstName} {employee.lastName}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid2>
            ))}
        </Grid2>
        </>
    );
    };

export default EmployeePicker;
